import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { environment } from "@root/src/environments/environment";
import { CheckboxComponent } from "@components/checkbox";
import { DialogService } from "primeng/dynamicdialog";
import { Filters } from "@components/views/filters/filters";
import { buildFilterQuery } from "@app/utils/_msf";
import { ServerTranslatePipe } from "@app/utils/pipes/server-translate-pipe";
import { UserService, LockPermissionService, LockPermission, LockPermissionType } from "@6grain/masofa-monolith-api-client/identity";
import { ServerTranslateService } from "@services/server-translate.service";
import { InputComponent } from '@components/input';
import { MsfSelect } from '@components/select/select';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ListPageWrapper } from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import { Loading } from '@components/ui/loading/loading';
import { TooltipModule } from 'primeng/tooltip';
import { IMsfFormBuilder } from "@components/form-builder/form-builder";

interface User {
    id: string;
    username: string;
    fullName?: string;
    email: string;
    createdDate: string;
    createdDateObj?: Date | null;
    role: string;
    roleId?: string;
    period: string;
    lockoutStart?: Date | null;
    lockoutEnd?: Date | null;
    status: 'active' | 'blocked';
    selected?: boolean;
    organizationId?: string;
    originalData?: any;
}

interface UserFilter {
    id: string;
    fullName: string;
}

interface EntityInfo {
    fullName: string;
    name: string;
    displayName: string;
    namespace?: string;
    description?: string;
    canBeLocked: boolean;
}

interface ActionInfo {
    name: string;
    displayName: string;
    description?: string;
    requiresEntityId?: boolean;
    applicableToEntities?: string[];
}

interface PermissionTypeInfo {
    name: string;
    displayName: string;
    description: string;
    requiresAction: boolean;
    requiresEntityId: boolean;
    value: number;
}

interface SystemMetadata {
    entities: EntityInfo[];
    actions: ActionInfo[];
    permissionTypes: PermissionTypeInfo[];
}

interface UserLockPermission {
    id?: string;
    userId: string;
    entityTypeName: string;
    entityAction?: string;
    entityId?: string;
    lockPermissionType: LockPermissionType;
}

interface EntityActionSelection {
    entity: EntityInfo;
    actions: { [actionName: string]: boolean };
    entityBlocked: boolean;
    instanceBlocked: boolean;
    permissionType: number;
    expanded?: boolean;
}

@Component({
    selector: 'app-lock-permissions',
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        ButtonModule,
        InputTextModule,
        TableModule,
        TagModule,
        PaginatorModule,
        FormsModule,
        CheckboxModule,
        DialogModule,
        SelectModule,
        ToastModule,
        CheckboxComponent,
        Filters,
        ServerTranslatePipe,
        InputComponent,
        MsfSelect,
        ProgressSpinner,
        ListPageWrapper,
        Loading,
        TooltipModule,
    ],
    providers: [MessageService, DialogService],
    templateUrl: './lock-permissions.component.html',
    styleUrl: './lock-permissions.component.scss'
})
export class LockPermissionsComponent implements OnInit, OnDestroy {
    @ViewChild(Filters) filtersComponent!: Filters;
    @ViewChild('lockPermissionTable') lockPermissionTable: any;

    // Data properties
    users: User[] = [];
    userFilters: UserFilter[] = [];
    lockPermissions: LockPermission[] = [];
    groupedLockPermissions: { [userId: string]: { user: User, permissions: LockPermission[] } } = {};
    totalLockPermissionRecords: number = 0;
    isLoading: boolean = false;
    loading = true;

    // System metadata
    systemMetadata: SystemMetadata = {
        entities: [],
        actions: [],
        permissionTypes: []
    };

    // Modal and form properties
    showLockPermissionDialog: boolean = false;
    showDeleteConfirmDialog: boolean = false;
    editingLockPermissionId: string | null = null;
    isEditMode: boolean = false;
    lockPermissionToDelete: LockPermission | null = null;
    isLoadingLockPermission: boolean = false;
    isSavingLockPermission: boolean = false;
    isDeletingLockPermission: boolean = false;

    // Multi-step modal state
    currentStep: number = 1;
    selectedUser: any = null;
    userLockPermissions: UserLockPermission[] = [];
    entityActionSelections: { [entityKey: string]: EntityActionSelection } = {};
    entityExpandedStates: { [entityKey: string]: { expanded: boolean } } = {};

    // Filters
    lockPermissionFilters: IMsfFormBuilder[] = [];
    lockPermissionFiltersData: any = {};

    // Pagination
    pageSize: number = 10;
    currentPage: number = 1;
    pageSizeOptions = [
        { label: '10', value: 10 },
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
    ];

    // Subject for unsubscribing
    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private http: HttpClient,
        private translate: TranslateService,
        private dialogService: DialogService,
        private messageService: MessageService,
        private serverTranslate: ServerTranslateService,
        private userService: UserService,
        private lockPermissionService: LockPermissionService,
    ) {}

    ngOnInit(): void {
        this.initializeFilters();
        this.loadUserFilters(); // Load optimized user filters first
        this.loadUsers();
        this.loadSystemMetadata();
        this.loadLockPermissions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Initialize filters
    initializeFilters(): void {
        // Use optimized user filters if available, otherwise fallback to users
        const userOptions = this.userFilters.length > 0 ? this.userFilters : this.users;
        const userOptionLabel = this.userFilters.length > 0 ? 'fullName' : 'username';
        
        this.lockPermissionFilters = [
            {
                name: 'userId',
                title: 'User',
                type: 'select',
                selectOptions: userOptions,
                options: {
                    select: {
                        optionValue: 'id',
                        optionLabel: userOptionLabel,
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'entityTypeName',
                title: 'Entity Type',
                type: 'select',
                selectOptions: this.systemMetadata.entities.filter(e => e.canBeLocked),
                options: {
                    select: {
                        optionValue: 'fullName',
                        optionLabel: 'displayName',
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'lockPermissionType',
                title: 'Permission Type',
                type: 'select',
                selectOptions: this.getPermissionTypeOptionsForFilter(),
                options: {
                    select: {
                        optionValue: 'value',
                        optionLabel: 'label',
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'entityAction',
                title: 'Action',
                type: 'select',
                selectOptions: this.systemMetadata.actions,
                options: {
                    select: {
                        optionValue: 'name',
                        optionLabel: 'displayName',
                        translateBy: 'none'
                    }
                }
            }
        ];
    }

    // Update filters with current data
    updateFilters(): void {
        // Use optimized user filters if available, otherwise fallback to users
        const userOptions = this.userFilters.length > 0 ? this.userFilters : this.users;
        const userOptionLabel = this.userFilters.length > 0 ? 'fullName' : 'username';
        
        this.lockPermissionFilters = [
            {
                name: 'userId',
                title: 'User',
                type: 'select',
                selectOptions: userOptions,
                options: {
                    select: {
                        optionValue: 'id',
                        optionLabel: userOptionLabel,
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'entityTypeName',
                title: 'Entity Type',
                type: 'select',
                selectOptions: this.systemMetadata.entities.filter(e => e.canBeLocked),
                options: {
                    select: {
                        optionValue: 'fullName',
                        optionLabel: 'displayName',
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'lockPermissionType',
                title: 'Permission Type',
                type: 'select',
                selectOptions: this.getPermissionTypeOptionsForFilter(),
                options: {
                    select: {
                        optionValue: 'value',
                        optionLabel: 'label',
                        translateBy: 'none'
                    }
                }
            },
            {
                name: 'entityAction',
                title: 'Action',
                type: 'select',
                selectOptions: this.systemMetadata.actions,
                options: {
                    select: {
                        optionValue: 'name',
                        optionLabel: 'displayName',
                        translateBy: 'none'
                    }
                }
            }
        ];
    }

    // Load optimized user filters for dropdowns
    loadUserFilters(): void {
        const apiUrl = `${environment.api}/identity/User/GetUserFilters`;

        // Get access token
        let token = localStorage.getItem('access_token');
        if (!token) {
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key.includes('authnResult') || key.includes('oidc.user')) {
                    try {
                        const data = JSON.parse(localStorage.getItem(key) || '{}');
                        if (data.access_token) {
                            token = data.access_token;
                            break;
                        }
                    } catch (e) {
                    }
                }
            }
        }

        let headers = new HttpHeaders();
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        this.http.get<UserFilter[]>(apiUrl, {headers}).subscribe({
            next: (response) => {
                this.userFilters = response || [];
                this.updateFilters();
                this.cdr.detectChanges();
            },
            error: (error) => {
                console.error('Error loading user filters:', error);
                this.loadUsers();
            }
        });
    }

    loadUsers(): void {
        const requestParams = {
            take: 1000,
            offset: 0,
            filters: buildFilterQuery({})
        };

        this.userService.identityUserGetByQueryPost(requestParams).subscribe({
            next: (response) => {
                if (response && Array.isArray(response)) {
                    this.users = response.map((user: any) => {
                        const fullName = user.fullName || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '') || user.userName || '';
                        const lockoutStart = this.parseLockoutDate(user.lockoutStart ?? user.scheduledLockoutStart);
                        const lockoutEnd = this.parseLockoutDate(user.lockoutEnd ?? user.scheduledLockoutEnd);
                        
                        return {
                            id: user.id || '',
                            username: user.userName || '',
                            fullName: fullName,
                            email: user.email || '',
                            createdDate: user.createAt ? new Date(user.createAt).toLocaleDateString('ru-RU') : '',
                            createdDateObj: user.createAt ? new Date(user.createAt) : null,
                            role: user.roles?.[0] || '',
                            roles: user.roles,
                            period: this.formatLockPeriod(lockoutStart, lockoutEnd),
                            lockoutStart,
                            lockoutEnd,
                            status: user.status || 'active',
                            selected: false,
                            organizationId: user.organizationId || '',
                            originalData: user
                        };
                    });

                    // Update filters after users are loaded
                    this.updateFilters();
                }
            },
            error: (error) => {
                // Error loading users
            }
        });
    }

    // Load system metadata
    loadSystemMetadata(): void {
        // Get access token
        let token = localStorage.getItem('access_token');
        if (!token) {
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key.includes('authnResult') || key.includes('oidc.user')) {
                    try {
                        const data = JSON.parse(localStorage.getItem(key) || '{}');
                        if (data.access_token) {
                            token = data.access_token;
                            break;
                        }
                    } catch (e) {
                    }
                }
            }
        }

        let headers = new HttpHeaders();
        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        this.http.get<SystemMetadata>(`${environment.api}/common/systemmetadata/entities-and-actions`, { headers }).subscribe({
            next: (response) => {
                this.systemMetadata = {
                    entities: response.entities || [],
                    actions: this.filterRelevantActions(response.actions || []),
                    permissionTypes: response.permissionTypes || []
                };
                
                this.updateFilters();
                this.cdr.detectChanges();
            },
            error: (error) => {
                console.error('Error loading system metadata:', error);
                // Set default values if loading fails
                this.systemMetadata = {
                    entities: [],
                    actions: [],
                    permissionTypes: [
                        { name: 'Action', displayName: 'Action', description: 'Block specific actions', requiresAction: true, requiresEntityId: false, value: 0 },
                        { name: 'Entity', displayName: 'Entity', description: 'Block entire entity', requiresAction: false, requiresEntityId: false, value: 1 },
                        { name: 'Instance', displayName: 'Instance', description: 'Block specific instances', requiresAction: false, requiresEntityId: true, value: 2 }
                    ]
                };
                
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Error'),
                    detail: this.translate.instant('Failed to load system metadata')
                });
                this.cdr.detectChanges();
            }
        });
    }

    // Возвращаем все действия с бэкенда
    filterRelevantActions(actions: ActionInfo[]): ActionInfo[] {
        return actions.map(action => ({
            name: action.name,
            displayName: action.displayName
        }));
    }

    // Load lock permissions with filters using new grouped API
    loadLockPermissions(): void {
        this.isLoading = true;
        
        const requestParams = {
            take: this.pageSize,
            offset: (this.currentPage - 1) * this.pageSize,
            userId: this.lockPermissionFiltersData.userId || null,
            entityTypeName: this.lockPermissionFiltersData.entityTypeName || null,
            entityAction: this.lockPermissionFiltersData.entityAction || null,
            lockPermissionType: this.lockPermissionFiltersData.lockPermissionType || null,
            userNameSearch: this.lockPermissionFiltersData.userNameSearch || null
        };

        // Use API client for grouped lock permissions
        this.lockPermissionService.identityLockPermissionGroupedPost(requestParams).subscribe({
            next: (response) => {
                this.groupedLockPermissions = {};

                // Convert grouped response to our format
                if (response.data && Array.isArray(response.data)) {
                    response.data.forEach((group: any) => {
                        this.groupedLockPermissions[group.user.id] = {
                            user: group.user,
                            permissions: group.permissions || []
                        };
                    });
                }

                // totalRecords теперь содержит количество пользователей (не записей!)
                // Бэкенд группирует по пользователям и применяет пагинацию к пользователям
                this.totalLockPermissionRecords = response.totalRecords || 0;

                // Fallback: if API doesn't provide total records, use data length
                // Note: This only works correctly if all data fits in one page
                if (this.totalLockPermissionRecords === 0 && response.data && response.data.length > 0) {
                    this.totalLockPermissionRecords = response.data.length;
                }

                this.isLoading = false;
                this.loading = false;
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.isLoading = false;
                this.loading = false;
                console.error('Error loading lock permissions:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Ошибка'),
                    detail: this.translate.instant('Failed to load lock permissions')
                });
                this.cdr.detectChanges();
            }
        });
    }

    // Multi-step modal methods
    addLockPermission(): void {
        this.editingLockPermissionId = null;
        this.isEditMode = false;
        this.currentStep = 1;
        this.selectedUser = null;
        this.userLockPermissions = [];
        this.entityActionSelections = {};
        this.showLockPermissionDialog = true;
    }

    editLockPermission(user: any): void {
        this.editingLockPermissionId = null;
        this.isEditMode = true;
        this.currentStep = 2; // Go directly to second step
        this.selectedUser = {
            id: user.id,
            label: user.fullName || user.userName,
            value: user.id
        };
        
        this.loadUserLockPermissions(user.id);
        this.showLockPermissionDialog = true;
    }

    // Get user options for select dropdown
    getUserOptions(): any[] {
        if (this.userFilters.length > 0) {
            return this.userFilters.map(user => ({
                label: user.fullName,
                value: user.id
            }));
        } else {
            return this.users.map(user => ({
                label: user.fullName || user.username,
                value: user.id
            }));
        }
    }

    onUserSelected(event: any): void {
        // Получаем ID пользователя из события
        const userId = event?.value || event;
        
        // Ищем пользователя в userFilters сначала, потом в users как fallback
        let userFilter = this.userFilters.find(u => u.id === userId);
        let userLabel = '';
        
        if (userFilter) {
            userLabel = userFilter.fullName;
            this.selectedUser = {
                id: userFilter.id,
                label: userLabel,
                value: userFilter.id
            };
        } else {
            // Fallback to users array
            const user = this.users.find(u => u.id === userId);
            if (user) {
                userLabel = user.fullName || user.username;
                this.selectedUser = {
                    id: user.id,
                    label: userLabel,
                    value: user.id
                };
            }
        }
    }

    goToNextStep(): void {
        if (this.currentStep === 1 && this.selectedUser) {
            // Извлекаем userId - может быть либо строка (GUID), либо объект {id, value, label}
            const userId = typeof this.selectedUser === 'string' 
                ? this.selectedUser 
                : (this.selectedUser.id || this.selectedUser.value);
            
            if (!userId) {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Error'),
                    detail: this.translate.instant('INVALID_USER_ID')
                });
                return;
            }
            
            this.currentStep = 2;
            this.loadUserLockPermissions(userId);
        }
    }

    goToPreviousStep(): void {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }

    loadUserLockPermissions(userId: string): void {
        this.isLoadingLockPermission = true;
        
        const filters = buildFilterQuery({ userId: userId });
        
        this.lockPermissionService.identityLockPermissionGetByQueryPost({
            filters: filters,
            take: 1000,
            offset: 0
        }).subscribe({
            next: (response: any) => {
                this.userLockPermissions = response || [];
                this.isEditMode = (this.userLockPermissions?.length || 0) > 0;
                this.initializeEntityActionSelections();
                this.isLoadingLockPermission = false;
            },
            error: (error: any) => {
                // Error loading user lock permissions
                this.userLockPermissions = [];
                this.isEditMode = false;
                this.initializeEntityActionSelections();
                this.isLoadingLockPermission = false;
            }
        });
    }

    initializeEntityActionSelections(): void {
        this.entityActionSelections = {};
        
        // Initialize all lockable entities
        this.systemMetadata.entities.forEach(entity => {
            if (entity.canBeLocked) {
                this.entityActionSelections[entity.fullName] = {
                    entity: entity,
                    actions: {},
                    entityBlocked: false,
                    instanceBlocked: false,
                    permissionType: 1, // Default to Entity
                    expanded: false
                };
                
                // Initialize all actions
                this.systemMetadata.actions.forEach(action => {
                    this.entityActionSelections[entity.fullName].actions[action.name] = false;
                });
            }
        });

        // Fill existing permissions
        this.userLockPermissions.forEach(permission => {
            const entityKey = permission.entityTypeName;
            if (this.entityActionSelections[entityKey]) {
                switch (permission.lockPermissionType) {
                    case 0: // Action
                        this.entityActionSelections[entityKey].permissionType = 0;
                        if (permission.entityAction) {
                            this.entityActionSelections[entityKey].actions[permission.entityAction] = true;
                        }
                        break;
                    case 1: // Entity
                        this.entityActionSelections[entityKey].permissionType = 1;
                        this.entityActionSelections[entityKey].entityBlocked = true;
                        break;
                    case 2: // Instance
                        this.entityActionSelections[entityKey].permissionType = 2;
                        this.entityActionSelections[entityKey].instanceBlocked = true;
                        break;
                }
            }
        });
    }

    getPermissionTypeOptions(): any[] {
        
        if (!this.systemMetadata.permissionTypes || this.systemMetadata.permissionTypes.length === 0) {
            // Используем translate для получения переведенных значений
            const actionLabel = this.translate.instant('Action');
            const entityLabel = this.translate.instant('Entity');
            const instanceLabel = this.translate.instant('Instance');
            
            return [
                { label: actionLabel, value: 0, translateKey: 'Action' },
                { label: entityLabel, value: 1, translateKey: 'Entity' },
                { label: instanceLabel, value: 2, translateKey: 'Instance' }
            ];
        }
        
        const options = (this.systemMetadata.permissionTypes || []).map(type => {
            const labelKey = type.displayName || type.name || 'Unknown';
            const translatedLabel = this.translate.instant(labelKey);
            
            return {
                label: translatedLabel,
                value: type.value,
                translateKey: labelKey
            };
        });
        
        return options;
    }
    
    getPermissionTypeOptionsForFilter(): any[] {
        // Для фильтров используем тот же метод, но с обновлением при смене языка
        return this.getPermissionTypeOptions();
    }

    // Entity action methods
    onEntityBlockChange(entityKey: string, value: boolean): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Устанавливаем новое значение
        selection.entityBlocked = value;
        
        if (value) {
            // Если включаем блокировку всей сущности, отключаем остальные
            selection.instanceBlocked = false;
            Object.keys(selection.actions).forEach(action => {
                selection.actions[action] = false;
            });
        }
    }

    onInstanceBlockChange(entityKey: string, value: boolean): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Устанавливаем новое значение
        selection.instanceBlocked = value;
        
        if (value) {
            // Если включаем блокировку инстансов, отключаем остальные
            selection.entityBlocked = false;
            Object.keys(selection.actions).forEach(action => {
                selection.actions[action] = false;
            });
        }
    }

    onActionBlockChange(entityKey: string, actionName: string, value: boolean): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Устанавливаем новое значение
        selection.actions[actionName] = value;
        
        if (value) {
            // Если включаем блокировку действия, отключаем блокировки сущности и инстансов
            selection.entityBlocked = false;
            selection.instanceBlocked = false;
        }
    }

    toggleEntityBlock(entityKey: string): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Переключаем состояние
        selection.entityBlocked = !selection.entityBlocked;
        
        if (selection.entityBlocked) {
            // Если включаем блокировку всей сущности, отключаем остальные
            selection.instanceBlocked = false;
            Object.keys(selection.actions).forEach(action => {
                selection.actions[action] = false;
            });
        }
    }

    toggleInstanceBlock(entityKey: string): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Переключаем состояние
        selection.instanceBlocked = !selection.instanceBlocked;
        
        if (selection.instanceBlocked) {
            // Если включаем блокировку инстансов, отключаем остальные
            selection.entityBlocked = false;
            Object.keys(selection.actions).forEach(action => {
                selection.actions[action] = false;
            });
        }
    }

    toggleActionBlock(entityKey: string, actionName: string): void {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return;
        
        // Переключаем состояние
        selection.actions[actionName] = !selection.actions[actionName];
        
        if (selection.actions[actionName]) {
            // Если включаем блокировку действия, отключаем блокировки сущности и инстансов
            selection.entityBlocked = false;
            selection.instanceBlocked = false;
        }
    }

    setEntityPermissionType(entityKey: string, event: any): void {
        const permissionType = event?.value || event;
        const selection = this.entityActionSelections[entityKey];
        
        if (!selection) return;
        
        // Обновляем permissionType
        selection.permissionType = permissionType;
        
        // Сбрасываем все блокировки
        selection.entityBlocked = false;
        selection.instanceBlocked = false;
        Object.keys(selection.actions).forEach(action => {
            selection.actions[action] = false;
        });
        
        // Устанавливаем нужный тип
        switch (permissionType) {
            case 1: // Entity
                selection.entityBlocked = true;
                break;
            case 2: // Instance
                selection.instanceBlocked = true;
                break;
            case 0: // Action
                // Оставляем возможность выбора действий
                break;
        }
    }

    // Save methods
    saveMultiStepLockPermission(): void {
        // Validate that we have a user selected
        if (!this.selectedUser) {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('Error'),
                detail: this.translate.instant('Please select a user')
            });
            return;
        }

        // Get the userId - may be string (GUID) or object {id, value, label}
        const userId = typeof this.selectedUser === 'string' 
            ? this.selectedUser 
            : (this.selectedUser.id || this.selectedUser.value);
        
        if (!userId) {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('Error'),
                detail: this.translate.instant('INVALID_USER_ID')
            });
            return;
        }

        this.isSavingLockPermission = true;

        // Формируем модель для отправки на бэкенд
        const saveData = {
            userId: userId,
            entityPermissions: this.buildEntityPermissionsModel()
        };

        // Use the API client service to send the request
        this.lockPermissionService.identityLockPermissionSaveUserPermissionsPost(saveData).subscribe({
            next: (response) => {
                // Lock permissions saved successfully
                this.isSavingLockPermission = false;
                this.showLockPermissionDialog = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('Success'),
                    detail: this.translate.instant('Lock permissions saved successfully')
                });
                
                // Reset user filter after successful save
                this.lockPermissionFiltersData.userId = null;
                this.loadLockPermissions();
                this.cdr.detectChanges();
            },
            error: (error) => {
                // Error saving lock permissions
                this.isSavingLockPermission = false;
                const rawMessage = error?.error?.message || error?.message || 'Failed to save lock permissions';
                const translated = this.translate.instant(rawMessage);
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Error'),
                    detail: translated || rawMessage
                });
                this.cdr.detectChanges();
            }
        });
    }

    // Формируем модель блокировок для отправки на бэкенд
    buildEntityPermissionsModel(): any[] {
        const entityPermissions: any[] = [];

        Object.keys(this.entityActionSelections).forEach(entityKey => {
            const selection = this.entityActionSelections[entityKey];
            if (!selection) return;

            const entityPermission: any = {
                entityTypeName: entityKey,
                lockPermissionType: selection.permissionType || 0,
                entityBlocked: selection.entityBlocked || false,
                instanceBlocked: selection.instanceBlocked || false,
                blockedActions: []
            };

            // Если тип блокировки - Action, собираем заблокированные действия
            if (selection.permissionType === 0 && selection.actions) {
                Object.keys(selection.actions).forEach(actionName => {
                    if (selection.actions[actionName]) {
                        entityPermission.blockedActions.push(actionName);
                    }
                });
            }

            // Добавляем только если есть какие-то блокировки
            if (entityPermission.entityBlocked || entityPermission.instanceBlocked || entityPermission.blockedActions.length > 0) {
                entityPermissions.push(entityPermission);
            }
        });

        return entityPermissions;
    }

    buildSaveOperations(): any[] {
        const operations: any[] = [];
        
        Object.keys(this.entityActionSelections).forEach(entityKey => {
            const selection = this.entityActionSelections[entityKey];
            
            // Check entity block
            if (selection.entityBlocked) {
                const existingPermission = this.userLockPermissions.find(p => 
                    p.entityTypeName === entityKey && p.lockPermissionType === 1
                );
                
                if (!existingPermission) {
                    operations.push({
                        type: 'create',
                        data: {
                            userId: this.selectedUser.id,
                            entityTypeName: entityKey,
                            lockPermissionType: 1
                        }
                    });
                }
            } else {
                const existingPermission = this.userLockPermissions.find(p => 
                    p.entityTypeName === entityKey && p.lockPermissionType === 1
                );
                
                if (existingPermission) {
                    operations.push({
                        type: 'delete',
                        id: existingPermission.id
                    });
                }
            }

            // Check instance block
            if (selection.instanceBlocked) {
                const existingPermission = this.userLockPermissions.find(p => 
                    p.entityTypeName === entityKey && p.lockPermissionType === 2
                );
                
                if (!existingPermission) {
                    operations.push({
                        type: 'create',
                        data: {
                            userId: this.selectedUser.id,
                            entityTypeName: entityKey,
                            lockPermissionType: 2
                        }
                    });
                }
            } else {
                const existingPermission = this.userLockPermissions.find(p => 
                    p.entityTypeName === entityKey && p.lockPermissionType === 2
                );
                
                if (existingPermission) {
                    operations.push({
                        type: 'delete',
                        id: existingPermission.id
                    });
                }
            }

            // Check action blocks
            Object.keys(selection.actions).forEach(actionName => {
                if (selection.actions[actionName]) {
                    const existingPermission = this.userLockPermissions.find(p => 
                        p.entityTypeName === entityKey && 
                        p.lockPermissionType === 0 && 
                        p.entityAction === actionName
                    );
                    
                    if (!existingPermission) {
                        operations.push({
                            type: 'create',
                            data: {
                                userId: this.selectedUser.id,
                                entityTypeName: entityKey,
                                entityAction: actionName,
                                lockPermissionType: 0
                            }
                        });
                    }
                } else {
                    const existingPermission = this.userLockPermissions.find(p => 
                        p.entityTypeName === entityKey && 
                        p.lockPermissionType === 0 && 
                        p.entityAction === actionName
                    );
                    
                    if (existingPermission) {
                        operations.push({
                            type: 'delete',
                            id: existingPermission.id
                        });
                    }
                }
            });
        });

        return operations;
    }

    executeSaveOperations(operations: any[]): void {
        if (operations.length === 0) {
            this.isSavingLockPermission = false;
            this.showLockPermissionDialog = false;
            this.loadLockPermissions();
            this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: this.translate.instant('Lock permissions saved successfully')
            });
            return;
        }

        const operation = operations.shift();
        
        switch (operation.type) {
            case 'create':
                this.lockPermissionService.identityLockPermissionCreatePost(operation.data).subscribe({
                    next: () => {
                        this.executeSaveOperations(operations);
                    },
                    error: (error: any) => {
                        console.error('Error creating lock permission:', error);
                        this.isSavingLockPermission = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: this.translate.instant('Error saving lock permissions')
                        });
                    }
                });
                break;
                
            case 'delete':
                this.lockPermissionService.identityLockPermissionDeleteIdDelete(operation.id).subscribe({
                    next: () => {
                        this.executeSaveOperations(operations);
                    },
                    error: (error: any) => {
                        console.error('Error deleting lock permission:', error);
                        this.isSavingLockPermission = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: this.translate.instant('Error saving lock permissions')
                        });
                    }
                });
                break;
        }
    }

    // Delete methods
    deleteLockPermission(lockPermission: LockPermission): void {
        this.lockPermissionToDelete = lockPermission;
        this.showDeleteConfirmDialog = true;
    }

    confirmDeleteLockPermission(): void {
        if (!this.lockPermissionToDelete?.id) {
            return;
        }

        this.isDeletingLockPermission = true;

        this.lockPermissionService.identityLockPermissionDeleteIdDelete(this.lockPermissionToDelete.id).subscribe({
            next: (response) => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('Success'),
                    detail: this.translate.instant('Lock permission deleted successfully')
                });

                this.isDeletingLockPermission = false;
                this.showDeleteConfirmDialog = false;
                this.lockPermissionToDelete = null;
                this.loadLockPermissions();
            },
            error: (error) => {
                this.isDeletingLockPermission = false;
                const rawMessage = error?.error?.message || error?.message || 'Failed to delete lock permission';
                const translated = this.translate.instant(rawMessage);
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Error'),
                    detail: translated || rawMessage
                });
                this.cdr.detectChanges();
            }
        });
    }

    // Group lock permissions by user (now handled by backend)
    groupLockPermissionsByUser(): void {
        // This method is no longer needed as grouping is done on the backend
        // Keeping for backward compatibility
    }

    // Get grouped data for table
    getGroupedDataForTable(): any[] {
        return Object.values(this.groupedLockPermissions);
    }

    // Подсчет заблокированных сущностей (тип блокировки = Entity или Instance)
    getBlockedEntitiesCount(permissions: LockPermission[]): number {
        return permissions.filter(p => p.lockPermissionType === 1 || p.lockPermissionType === 2).length;
    }

    // Подсчет заблокированных действий (тип блокировки = Action)
    getBlockedActionsCount(permissions: LockPermission[]): number {
        return permissions.filter(p => p.lockPermissionType === 0).length;
    }

    // Utility methods
    getEntityKeys(): string[] {
        return Object.keys(this.entityActionSelections);
    }

    getEntityDisplayName(entityKey: string): string {
        const selection = this.entityActionSelections[entityKey];
        return selection?.entity?.displayName || entityKey;
    }

    hasActionBlocks(entityKey: string): boolean {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return false;
        
        return Object.values(selection.actions).some((blocked: any) => blocked);
    }

    hasAnyBlocks(entityKey: string): boolean {
        const selection = this.entityActionSelections[entityKey];
        if (!selection) return false;
        
        return selection.entityBlocked || selection.instanceBlocked || this.hasActionBlocks(entityKey);
    }

    toggleEntityExpanded(entityKey: string): void {
        if (!this.entityExpandedStates[entityKey]) {
            this.entityExpandedStates[entityKey] = { expanded: false };
        }
        this.entityExpandedStates[entityKey].expanded = !this.entityExpandedStates[entityKey].expanded;
        
        if (!this.entityActionSelections[entityKey]) {
            this.entityActionSelections[entityKey] = {
                entity: {} as EntityInfo,
                actions: {},
                entityBlocked: false,
                instanceBlocked: false,
                permissionType: 1,
                expanded: false
            } as EntityActionSelection;
        }
        
        // Синхронизируем состояние
        this.entityActionSelections[entityKey].expanded = this.entityExpandedStates[entityKey].expanded;
    }

    getUserName(userId: string): string {
        const user = this.users.find(u => u.id === userId);
        return user ? user.username : userId;
    }

    getPermissionTypeName(permissionType: LockPermissionType): string {
        switch (permissionType) {
            case LockPermissionType.NUMBER_0:
                return this.translate.instant('Action');
            case LockPermissionType.NUMBER_1:
                return this.translate.instant('Entity');
            case LockPermissionType.NUMBER_2:
                return this.translate.instant('Instance');
            default:
                return this.translate.instant('Unknown');
        }
    }

    getTranslatedText(key: string): string {
        const translated = this.translate.instant(key);
        if (typeof translated === 'string' && translated !== key) {
            return translated;
        }
        return key;
    }

    private parseLockoutDate(value: any): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value;
        }

        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    private formatLockPeriod(start: Date | null, end: Date | null): string {
        if (start && end) {
            return `${this.formatDate(start)} - ${this.formatDate(end)}`;
        }

        if (start) {
            return this.formatDate(start);
        }

        if (end) {
            return this.formatDate(end);
        }

        return '';
    }

    private formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }

    private getCurrentLocale(): string {
        const translateStore = (this.translate as any)?.store;
        const currentLang = translateStore?.currentLang || this.translate.currentLang || this.translate.defaultLang || 'ru-RU';
        return currentLang.replace('_', '-');
    }

    // Filter methods
    applyLockPermissionFilters(): void {
        console.log('Applying filters:', this.lockPermissionFiltersData);
        
        // Если выбран тип, отличный от Action (0), сбрасываем action-фильтр
        if (this.lockPermissionFiltersData && this.lockPermissionFiltersData.lockPermissionType !== 0) {
            this.lockPermissionFiltersData.entityAction = null;
        }
        
        this.currentPage = 1;
        this.loadLockPermissions();
    }

    clearLockPermissionFilters(): void {
        this.lockPermissionFiltersData = {};
        this.loadLockPermissions();
    }

    // Dialog methods
    cancelLockPermissionDialog(): void {
        this.showLockPermissionDialog = false;
        this.editingLockPermissionId = null;
        this.isEditMode = false;
        this.currentStep = 1;
        this.selectedUser = null;
        this.entityActionSelections = {};
        this.loadLockPermissions();
    }

    cancelDeleteLockPermission(): void {
        this.showDeleteConfirmDialog = false;
        this.lockPermissionToDelete = null;
    }

    // Pagination
    onPageSizeChange(event: any): void {
        this.pageSize = event.value;
        this.currentPage = 1;
        this.loadLockPermissions();
    }

    // Обработка смены страницы таблицы
    onPageChange(event: any): void {
        // PrimeNG p-table передает event.first (index), event.rows (pageSize)
        // Пересчитываем текущую страницу (1-based)
        const first = event?.first ?? 0;
        const rows = event?.rows ?? this.pageSize;
        this.pageSize = rows;
        this.currentPage = Math.floor(first / rows) + 1;
        this.loadLockPermissions();
    }
}
